<template>
  <div v-if="survey"
       class="survey-summary">
    <div class="summary-survey">
      <div class="survey-meta">
        <p class="duration">
          <span class="timezone">{{timezoneName}}</span>{{ computedStartDate }} ~ {{ computedEndDate }}
        </p>
        <h2 class="name">
          {{ survey.surveyName }}
        </h2>
      </div>
      <div class="survey-item">
        <h3>
          {{ $t('userResearch.survey.detail.information') }}
        </h3>
        <table class="item">
          <tr>
            <th class="info-1"
                rowspan="4">
              {{ $t('userResearch.survey.detail.target') }}
            </th>
            <th class="info-2">
              {{ $t('userResearch.survey.detail.device') }}
            </th>
            <td>
              {{ survey.targetDevices.map(device => $t(`common.deviceType.${device}2`)).join(', ') }}
            </td>
          </tr>
          <tr>
            <th class="info-2">
              {{ $t('userResearch.survey.detail.page') }}
            </th>
            <td>
              {{ $t(`userResearch.survey.create.targetScope.${survey.targetScope}`) }}
              <template v-if="survey.targetScope !== SURVEY_CONSTANT.TARGET_SCOPE_ALL">
                <p class="info-p-title">
                  {{ $t(`common.matchType.${survey.targetMatchType}`) }}
                </p>
                <p>
                  {{ $t('common.url') }} : {{ survey.targetUrl }}
                </p>
                <p v-if="['ends', 'end',].includes(survey.targetMatchType)">
                  {{ $t('common.matchType.endsMatchStringLabel') }} : {{ survey.targetMatchString }}
                </p>
                <p v-else-if="['contains', 'contain'].includes(survey.targetMatchType)">
                  {{ $t('common.matchType.containsMatchStringLabel') }} : {{ survey.targetMatchString }}
                </p>
                <p v-else-if="['regExp', 'regexp'].includes(survey.targetMatchType)">
                  {{ $t('common.matchType.regExpMatchStringLabel') }} : {{ survey.targetMatchString }}
                </p>
              </template>
            </td>
          </tr>
          <tr>
            <th class="info-2">
              {{ $t('userResearch.survey.detail.timing') }}
            </th>
            <td v-if="survey.popupLoading">
              {{ $t(`userResearch.survey.create.popupLoading.${camelize(survey.popupLoading)}`) }}
            </td>
            <td v-else>
              {{ $t('userResearch.survey.create.popupLoading.afterLoad') }}
            </td>
          </tr>
          <tr>
            <th class="info-2">
              {{ $t('userResearch.survey.detail.customer') }}
            </th>
            <td v-if="survey.campaignParams === null">
              {{ $t('userResearch.survey.detail.allCustomer') }}
            </td>
            <td v-else>
              {{ $t('userResearch.survey.detail.campaignCustomer') }}
              <ul>
                <template v-for="utm in Object.entries(survey.campaignParams)">
                  <li v-if="utm[1]"
                      :key="`summary-campaign-${utm[0]}`">
                    {{ utm[0] }}={{ utm[1].value }} ({{ $t(`userResearch.survey.create.utmCondition.${utm[1].matchType}`) }})
                  </li>
                </template>
              </ul>
            </td>
          </tr>
          <tr>
            <th class="info-1"
                rowspan="2">
              {{ $t('userResearch.survey.detail.period') }}
            </th>
            <th class="info-2">
              {{ $t('userResearch.survey.detail.statDate') }}
            </th>
            <td>
              {{ computedStartDate }}
            </td>
          </tr>
          <tr>
            <th class="info-2">
              {{ $t('userResearch.survey.detail.endDate') }}
            </th>
            <td>
              {{ computedEndDate }}
            </td>
          </tr>
          <tr>
            <th class="info-1"
                rowspan="2">
              {{ $t('userResearch.survey.detail.style') }}
            </th>
            <th class="info-2">
              {{ $t('userResearch.survey.detail.form') }}
            </th>
            <td>
              <template v-if="survey.popupStyle === SURVEY_CONSTANT.POPUP_STYLE_POPUP_ALWAYS">
                {{ $t('userResearch.survey.create.popover') }}
              </template>
              <template v-else-if="survey.popupStyle === SURVEY_CONSTANT.POPUP_STYLE_MODAL_ONETIME">
                {{ $t('userResearch.survey.create.fullScreen') }}
              </template>
            </td>
          </tr>
          <tr>
            <th class="info-2">
              {{ $t('userResearch.survey.detail.language') }}
            </th>
            <td>
              <template v-if="survey.lang === ''">
                {{ $t('userResearch.survey.create.languageType.browser') }}
              </template>
              <template v-else>
                <template v-if="survey.lang === SURVEY_CONSTANT.LANG_EN">
                  English
                </template>
                <template v-else-if="survey.lang === SURVEY_CONSTANT.LANG_JA">
                  日本語
                </template>
                <template v-else>
                  한국어
                </template>
              </template>
            </td>
          </tr>
        </table>
      </div>

      <div v-if="survey.surveyIntro"
           class="survey-item">
        <h3>
          {{ $t('userResearch.survey.detail.opening') }}
        </h3>
        <p class="item" v-html="survey.surveyIntro.replace(/\n/g, '<br>')"></p>
      </div>

      <div class="survey-item">
        <h3>
          {{ $t('userResearch.survey.detail.questions') }}
        </h3>
        <div class="items">
          <div v-for="(question, index) in survey.questions"
               :key="`question-${index}`"
               :class="{'item': !question.deleted}">
            <template v-if="!question.deleted">
              <div class="index">
                No. {{ index + 1 }}
              </div>
              <h4>
                {{ question.question }}
              </h4>
              <p v-if="question.questionType === SURVEY_CONSTANT.ITEM_TYPE_CHECKBOX"
                 class="description">
                {{ $t('userResearch.survey.detail.multiple') }}
              </p>
              <p v-if="!question.require"
                 class="description">
                {{ $t('common.optional') }}
              </p>
              <div v-if="question.questionType === SURVEY_CONSTANT.ITEM_TYPE_RADIO || question.questionType === SURVEY_CONSTANT.ITEM_TYPE_CHECKBOX"
                   class="options">
                <ul class="option">
                  <li v-for="(option, i) in question.answerOptions"
                      :key="`option-${index}-${i}`">
                    <ui-checkbox v-if="question.questionType === SURVEY_CONSTANT.ITEM_TYPE_CHECKBOX" />
                    <ui-radio v-else />
                    {{ option }}
                  </li>
                  <li v-if="question.answerOther"
                      class="other">
                    <ui-checkbox v-if="question.questionType === SURVEY_CONSTANT.ITEM_TYPE_CHECKBOX" />
                    <ui-radio v-else />
                    <span class="other-text">{{ $t('userResearch.survey.create.other') }} :</span>
                    <ui-input-text :type="'text'"
                                   :placeholder="' '"
                                   :readonly="true" />
                  </li>
                </ul>
              </div>
              <div v-if="question.questionType === SURVEY_CONSTANT.ITEM_TYPE_TEXT"
                   class="options">
                <textarea readonly></textarea>
              </div>
              <div v-if="question.questionType === SURVEY_CONSTANT.ITEM_TYPE_RANGE"
                   class="options">
                <div class="preference">
                  <div class="numbers">
                    <div v-for="(el, number) in Array(question.answerOptions.maxValue - question.answerOptions.minValue + 1)"
                         :key="`option-${index}-${number}`"
                         class="number">
                      <div class="num-text">{{ number + question.answerOptions.minValue }}</div>
                      <ui-radio />
                    </div>
                  </div>
                  <div v-if="question.answerOptions.minText"
                       class="description">
                    {{ question.answerOptions.minText }}
                  </div>
                  <div v-if="question.answerOptions.maxText"
                       class="description">
                    {{ question.answerOptions.maxText }}
                  </div>
                </div>
              </div>
            </template>
          </div>
        </div>
      </div>

      <div class="survey-item">
        <h3>
          {{ $t('userResearch.survey.detail.closing') }}
        </h3>
        <p class="item" v-html="survey.surveyClosing.replace(/\n/g, '<br>')">
        </p>
      </div>
    </div>
  </div>
</template>

<script>
import { camelize, getOffset, utcToLocalTimezone } from '@/libs/utils'

import UiInputText from '@/components/ui/UiInputText'
import UiCheckbox from '@/components/ui/UiCheckbox'
import UiRadio from '@/components/ui/UiRadio'
import { SURVEY_CONSTANT } from '@/libs/constants'
import { format } from 'date-fns'

export default {
  name: 'SurveySummary',
  components: {
    UiInputText,
    UiCheckbox,
    UiRadio
  },
  created () {
    this.$emit('loaded', true)
  },
  methods: {
    camelize (str) {
      return camelize(str)
    }
  },
  props: [
    'survey',
    'timezoneName'
  ],
  computed: {
    timezone () {
      return getOffset(this.timezoneName)
    },
    computedStartDate () {
      return format(utcToLocalTimezone(this.survey.startDate, this.timezoneName), 'YYYY-MM-DD')
    },
    computedEndDate () {
      return format(utcToLocalTimezone(this.survey.endDate, this.timezoneName), 'YYYY-MM-DD')
    }
  },
  data () {
    return {
      SURVEY_CONSTANT: SURVEY_CONSTANT,
      deletingSurvey: null
    }
  }
}
</script>

<style lang="scss" scoped>
.survey-summary {
  .summary-survey {
    width: 970px;
    margin: 0 auto;
    padding-bottom: 200px;

    .survey-meta {
      text-align: center;

      .duration {
        padding: 60px 0 12px;
        font-size: 16px;
        color: #767676;

        & .timezone {
          font-size: 14px;
          font-weight: normal;
          font-stretch: normal;
          font-style: normal;
          line-height: 1;
          letter-spacing: normal;
          color: #767676;

          &:after {
            content: "";
            display: inline-block;
            vertical-align: middle;
            width: 1px;
            height: 12px;
            background-color: #ccc;
            margin: 0 15px;
          }
        }
      }

      .name {
        font-size: 32px;
        font-weight: 500;
        line-height: 1.31;
        color: #2f2f2f;
        padding-bottom: 10px;
      }
    }

    .survey-item {
      h3 {
        margin: 60px 0 16px;
        font-size: 16px;
        font-weight: 500;
        color: #ec0047;
      }

      table.item {
        background-color: #fff;
        width: 100%;
        border-collapse: collapse;

        th,
        td {
          border-top: 1px solid #d7d7d7;
          border-bottom: 1px solid #d7d7d7;
          text-align: left;
          padding: 18px;
          vertical-align: top;
        }

        .info-1 {
          font-size: 14px;
          font-weight: 500;
          color: #666;
          vertical-align: middle;
          padding-left: 30px;
          width: 132px;
        }

        .info-2 {
          font-size: 13px;
          color: #888;
          width: 117px;
        }

        td {
          padding: 12px;
          font-size: 15px;
          line-height: 1.6;
          color: #2f2f2f;

          p {
            font-size: 13px;
            line-height: 1.62;
            color: #888;

            &.info-p-title {
              margin: 10px 0 8px;
              font-weight: 500;
              color: #888;
            }
          }

          ul {
            margin-top: 10px;
            font-size: 13px;
            line-height: 1.62;
            color: #888;

            li {
              position: relative;
              padding-left: 8px;

              &::before {
                content: "";
                position: absolute;
                top: 9px;
                left: 0;
                width: 3px;
                height: 3px;
                background-color: #444;
              }

              &:not(:first-child) {
                margin-top: 6px;
              }
            }
          }
        }
      }

      p.item {
        background-color: #fff;
        border-top: 1px solid #d7d7d7;
        border-bottom: 1px solid #d7d7d7;
        padding: 30px;
        font-size: 15px;
        line-height: 1.6;
        color: #2f2f2f;
      }

      div.items {
        border-bottom: 1px solid #d7d7d7;

        div.item {
          background-color: #fff;
          border-top: 1px solid #d7d7d7;
          padding: 40px 30px;

          .index {
            font-size: 22px;
            font-weight: 500;
            color: #2f2f2f;
            margin-bottom: 22px;
          }

          h4 {
            font-size: 18px;
            font-weight: 500;
            line-height: 1.28;
            color: #2f2f2f;
          }

          p.description {
            font-size: 12px;
            line-height: 1;
            color: #0e90a5;
            margin: 6px 0;
          }

          .options {
            padding-top: 14px;

            textarea {
              width: calc(100% - 26px);
              height: calc(157px - 18px);
              margin: 5px 0 4px;
              padding: 8px 12px;
              border-radius: 6px;
              border: solid 1px #bbb;
              background-color: #fff;
              font-size: 14px;
              line-height: 1.57;
              color: #999;
              resize: none;
              cursor: default;
            }

            .option {
              li {
                margin: 11px 0 20px -10px;
                font-size: 14px;
                line-height: 1.36;
                color: #444;

                &:last-child {
                  margin-bottom: 0;
                }

                &.other {
                  margin-top: -15px;
                  font-size: 14px;
                  line-height: 1.36;
                  color: #444;

                  .other-text {
                    display: inline-block;
                    width: 48px;
                  }

                  .input--wrap {
                    width: calc(100% - 85px);
                    margin-top: -12px;
                  }
                }
              }

              .ui_checkbox,
              .ui_radio {
                margin-left: 10px;
                margin-right: 2px;
                cursor: default;
              }

              .input--wrap {
                width: 784px;
              }
            }

            .preference {
              padding-bottom: 30px;

              &::after {
                content: "";
                display: block;
                clear: both;
              }

              .description {
                float: left;
                font-size: 14px;
                font-weight: bold;
                font-stretch: normal;
                font-style: normal;
                line-height: 1.36;
                letter-spacing: normal;
                color: #666;
                width: 120px;
                padding-top: 10px;
                padding-left: 30px;
                text-align: left;

                &:last-child {
                  float: right;
                  text-align: right;
                  padding-right: 30px;
                  padding-left: 0;
                }
              }

              .numbers {
                text-align: center;
                margin: 85px 0 0;
                display: table;
                width: 100%;

                .number {
                  display: table-cell;
                  vertical-align: top;
                  margin-right: 35px;
                  width: 18px;
                  position: relative;
                  text-align: center;

                  .num-text {
                    position: absolute;
                    top: -27px;
                    left: 50%;
                    transform: translateX(-50%);
                    font-size: 14px;
                    font-weight: normal;
                    font-stretch: normal;
                    font-style: normal;
                    line-height: 1.36;
                    letter-spacing: normal;
                    color: #444;
                  }

                  .ui_radio {
                    display: inline-block;
                    z-index: 2;
                    margin-left: 0;
                    width: 17px;
                    cursor: default;
                    background-color: #fff;
                  }

                  &:not(:last-child) {
                    .num-text {
                      margin-right: 40px;
                    }

                    &::after {
                      position: absolute;
                      z-index: 1;
                      top: 6px;
                      left: 50%;
                      display: block;
                      content: "";
                      width: 100%;
                      border-bottom: 2px dashed #aaa;
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
  }
}
</style>
